import { useCallback, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Logo, ViewportSurface } from '@wundo/ui';
import { PracticeSessionExperience } from '@/components/practice/PracticeSessionExperience.tsx';
import { practiceFailureMessage } from '@/components/practice/practiceFailureMessage.ts';
import { applyUserLocale } from '@/lib/locale.ts';
import { redirectIfPilotEnded } from '@/lib/pilotEnded.ts';
import { takeLinkClick } from '@/lib/linkClick.ts';
import { requireRoleplaySession } from '@/lib/roleplaySessionGuard.ts';
import { useRoleplaySession } from '@/lib/useRoleplaySession.ts';
import { m } from '@/paraglide/messages.js';

/*
 * Outside the app shell on purpose. A practice conversation is the only screen
 * in the app that asks for the whole window: the rail, the chat drawer and the
 * page title are all invitations to be somewhere else, and one of them is a
 * second conversation with Wundo running beside this one.
 *
 * It is also the only screen reached from outside the app, by someone who
 * clicked a link in a chat message and has no sign-in — which is why the guard
 * asks for the conversation rather than for the person.
 *
 * Starting from Oefenen no longer comes here: that opens the same conversation
 * in a modal on the page it was started from. This address stays for everybody
 * who has no page to open it over — a chat card, a mail link, a link visitor,
 * and a tab that was reloaded mid-conversation.
 */
export const Route = createFileRoute('/practice/session/$sessionId')({
  /*
   * Both questions at once: a pilot that has ended ends this screen too, and
   * asking in turn would put a round trip in front of the one visitor this
   * route exists for — somebody opening a link with no account at all.
   */
  beforeLoad: ({ context, params, location }) =>
    Promise.all([
      requireRoleplaySession(context.queryClient, params.sessionId, location.href),
      redirectIfPilotEnded(context.queryClient),
    ]).then(() => takeLinkClick(location.href)),
  component: PracticeSessionRoute,
});

function PracticeSessionRoute() {
  const { sessionId } = Route.useParams();
  return <PracticeSessionPage sessionId={sessionId} />;
}

export interface PracticeSessionPageProps {
  sessionId: string;
}

/**
 * One practice conversation at an address of its own, taking the whole window.
 *
 * Everything about the conversation itself is the experience below; what this
 * screen adds is what having an address of its own costs — the surface that
 * covers the window, the logo of an app that is not around it, the language the
 * conversation is in, and the ways opening it can fail.
 */
export function PracticeSessionPage({ sessionId }: PracticeSessionPageProps) {
  const navigate = useNavigate();
  const { session, isPending, failure } = useRoleplaySession(sessionId);

  /*
   * The language the conversation is in decides the language of the screen
   * around it. Set from the session rather than from the account, because this
   * is the one screen a visitor without an account reaches — there is nobody to
   * ask, and the session knows.
   */
  const language = session?.language;
  useEffect(() => {
    applyUserLocale(language);
  }, [language]);

  /*
   * Leaving here is going somewhere else: there is no page underneath to come
   * back to. Oefenen, plain — the feedback is read on this screen, so there is
   * nothing left to open over the list.
   */
  const leave = useCallback(() => {
    void navigate({ to: '/practice', search: {}, replace: true });
  }, [navigate]);

  return (
    <ViewportSurface
      as='main'
      aria-label={m.practice_session_title()}
      className='z-50 bg-canvas'
    >
      {isPending && (
        <PracticeSessionNotice>{m.practice_session_loading()}</PracticeSessionNotice>
      )}
      {failure !== null && (
        <PracticeSessionNotice>{practiceFailureMessage(failure)}</PracticeSessionNotice>
      )}
      {session !== undefined && (
        <PracticeSessionExperience
          key={session.id}
          session={session}
          brand={<Logo size='md' />}
          onLeave={leave}
        />
      )}
    </ViewportSurface>
  );
}

/**
 * Anything that is not a conversation: loading, a session that cannot be opened.
 *
 * It offers no way onward, because at this point there is no telling who is
 * reading it. Half the people who reach this screen came in on a link and have
 * no Oefenen — for them a link there is a login screen with no password, which
 * is a worse dead end than the sentence they are already looking at.
 */
function PracticeSessionNotice({ children }: { children: string }) {
  return (
    <div className='flex flex-1 flex-col items-center justify-center gap-4 px-5 text-center'>
      <p role='status' className='max-w-120 text-base text-ink-body'>
        {children}
      </p>
    </div>
  );
}
